import { Progress } from "@nimit9/signet-ui/components/progress"
import { StatStrip } from "@nimit9/signet-ui/components/stat-strip"
import { useMediaQuery } from "@nimit9/signet-ui/hooks/use-media-query"
import { createFileRoute } from "@tanstack/react-router"
import { useEffect, useMemo, useRef, useState } from "react"
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts"
import {
  AXIS_PROPS,
  BAR_CURSOR,
  BAR_RADIUS,
  CategoryLegend,
  CHART_MARGIN,
  ChartCard,
  type ChartConfig,
  ChartFrame,
  ChartTooltip,
  GRID_PROPS,
  legendItemsFromConfig,
  MoneyTooltip,
  Y_AXIS_WIDTH,
} from "@/components/charts"
import { Amount } from "@/components/common/amount"
import { useCompactCurrencyFormat } from "@/hooks/use-compact-currency-format"
import { useGoals } from "@/hooks/use-goals"
import { computeEmiSchedule } from "./-compute"
import { buildEmiHeadline } from "./-headline"
import { CalculatorLayout, NumberField, SliderField } from "./-layout"

export const Route = createFileRoute("/calculators/emi")({
  component: EMICalculator,
})

// Interest is extra cost, not a loss or a destructive action: amber, never red.
const CHART_CONFIG = {
  Principal: { label: "Principal", color: "var(--money-credit)" },
  Interest: { label: "Interest", color: "var(--accent-amber)" },
} satisfies ChartConfig

function EMICalculator() {
  const isDesktop = useMediaQuery("(min-width: 768px)")
  const fmtCompact = useCompactCurrencyFormat(2)
  const { data: goalsData } = useGoals()
  const [prefilled, setPrefilled] = useState(false)
  const [principal, setPrincipal] = useState(3000000)
  const [rate, setRate] = useState(9)
  const [tenureYears, setTenureYears] = useState(20)

  // Pre-fill from the first active EMI on the Goals page — once.
  const goalsApplied = useRef(false)
  useEffect(() => {
    if (goalsApplied.current || !goalsData?.emis?.length) return
    const active = goalsData.emis.find((e) => e.status === "active")
    if (!active) return
    goalsApplied.current = true
    const totalAmt = parseFloat(active.totalAmount)
    if (totalAmt > 0) setPrincipal(Math.round(totalAmt / 10000) * 10000)
    if (active.totalMonths > 0) setTenureYears(Math.round(active.totalMonths / 12))
    setPrefilled(true)
  }, [goalsData])

  const result = useMemo(
    () => computeEmiSchedule(principal, rate, tenureYears),
    [principal, rate, tenureYears],
  )
  const { emi, totalInterest, yearlyData } = result
  const totalPayment = emi * tenureYears * 12
  const headline = buildEmiHeadline(result, principal, tenureYears)
  const principalShare = totalPayment > 0 ? (principal / totalPayment) * 100 : 0
  const interestShare = totalPayment > 0 ? (totalInterest / totalPayment) * 100 : 0

  return (
    <CalculatorLayout
      title="EMI Calculator"
      headline={headline}
      isDesktop={isDesktop}
      prefilledNote={prefilled ? "Pre-filled from your EMIs" : null}
      inputs={
        <>
          <NumberField
            id="emi-principal"
            label="Loan amount (₹)"
            value={principal}
            onChange={setPrincipal}
            clamp={(n) => Math.max(10000, n)}
          />
          <SliderField
            id="emi-rate"
            label="Interest rate"
            value={rate}
            onChange={setRate}
            min={5}
            max={20}
            step={0.25}
            format={(n) => `${n}% p.a.`}
            minLabel="5%"
            maxLabel="20%"
          />
          <SliderField
            id="emi-tenure"
            label="Tenure"
            value={tenureYears}
            onChange={setTenureYears}
            min={1}
            max={30}
            format={(n) => `${n} years`}
            minLabel="1Y"
            maxLabel="30Y"
          />
          <div
            data-testid="emi-split"
            className="flex flex-col gap-2 rounded-lg bg-bg-elevated p-3"
          >
            <Progress value={Math.round(principalShare)} aria-label="Principal share of total" />
            <div className="flex justify-between text-caption">
              <span className="text-text-secondary">Principal {principalShare.toFixed(0)}%</span>
              <span className="text-accent-amber">Interest {interestShare.toFixed(0)}%</span>
            </div>
          </div>
        </>
      }
    >
      <StatStrip
        isDesktop={isDesktop}
        items={[
          { label: "Monthly EMI", value: <Amount value={Math.round(emi)} /> },
          {
            label: "Total interest",
            value: <Amount value={Math.round(totalInterest)} />,
            meta: "extra cost of borrowing",
            tone: "warn",
          },
          { label: "Total payment", value: <Amount value={Math.round(totalPayment)} /> },
        ]}
      />
      <ChartCard
        title="Year-wise breakdown"
        description="Principal and interest paid each year"
        legend={<CategoryLegend items={legendItemsFromConfig(CHART_CONFIG)} />}
        isEmpty={yearlyData.length === 0}
      >
        <ChartFrame config={CHART_CONFIG}>
          <BarChart data={yearlyData} margin={CHART_MARGIN} barCategoryGap="20%">
            <CartesianGrid {...GRID_PROPS} />
            <XAxis
              dataKey="year"
              {...AXIS_PROPS}
              interval={Math.max(0, Math.ceil(tenureYears / (isDesktop ? 10 : 5)) - 1)}
            />
            <YAxis {...AXIS_PROPS} width={Y_AXIS_WIDTH + 8} tickFormatter={fmtCompact} />
            <ChartTooltip content={<MoneyTooltip />} cursor={BAR_CURSOR} />
            <Bar
              dataKey="Principal"
              name="Principal"
              stackId="emi"
              fill="var(--color-Principal)"
              isAnimationActive={false}
            />
            <Bar
              dataKey="Interest"
              name="Interest"
              stackId="emi"
              fill="var(--color-Interest)"
              radius={BAR_RADIUS}
              isAnimationActive={false}
            />
          </BarChart>
        </ChartFrame>
      </ChartCard>
    </CalculatorLayout>
  )
}
